/**
 * Action inbox — the full list.
 *
 * The Overview card shows the head of this list; this is where the user sees
 * everything outstanding and can work through it. Every row links to the screen
 * that closes that specific gap, with the site and month prefilled.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { Shell } from '../components/Shell';
import { X } from '../components/v2/Icons';
import { TaskRow } from '../components/v2/TaskRow';
import { GROUP_ORDER, elapsedMonths } from '../components/v2/action-inbox';
import { useActionInbox } from '../components/v2/useActionInbox';
import { useT } from '../i18n';
import { Badge, Button, Callout, Card, Empty, PageHeader, Skeleton, Stat } from '../components/v2/ui';

function ActionInbox() {
  const navigate = useNavigate();
  const { t } = useT();
  const { tasks, counts, loading, matrix } = useActionInbox();

  const year = matrix?.year || new Date().getFullYear();
  const elapsed = elapsedMonths(year);
  const siteMonths = (matrix?.sites?.length || 0) * elapsed;
  const covered = siteMonths - counts.gaps;

  if (loading) {
    return (
      <div className="k2-wrap">
        <PageHeader title={t('inbox.title')} desc={t('inbox.loading')} />
        <div className="k2-g3" style={{ marginBottom: 24 }}>
          {[0, 1, 2].map((i) => <Card key={i}><Skeleton h={54} /></Card>)}
        </div>
        <Card><Skeleton h={160} /></Card>
      </div>
    );
  }

  const grouped = GROUP_ORDER
    .map((g) => ({ group: g, items: tasks.filter((t) => t.group === g) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="k2-wrap">
      <PageHeader
        title={t('inbox.title')}
        desc={t('inbox.desc')}
      >
        <Button variant="gh" icon={X.Home} onClick={() => navigate('/overview')}>{t('nav.overview')}</Button>
        <Button variant="mint" icon={X.Plus} onClick={() => navigate('/regjistrimi')}>{t('nav.monthly')}</Button>
      </PageHeader>

      <div className="k2-g3" style={{ marginBottom: 24 }}>
        <Stat
          label={t('inbox.stat.open')}
          value={counts.open}
          tone={counts.open === 0 ? undefined : 'warn'}
          foot={t(counts.open === 0 ? 'inbox.stat.openFootNone' : 'inbox.stat.openFoot')}
        />
        <Stat
          label={t('inbox.stat.covered', { year })}
          value={siteMonths ? `${covered}/${siteMonths}` : '—'}
          foot={siteMonths
            ? t('inbox.stat.coveredFoot', { pct: Math.round((covered / siteMonths) * 100) })
            : t('inbox.stat.noFacilities')}
        />
        <Stat
          label={t('inbox.stat.partial')}
          value={counts.partial}
          foot={counts.partial ? t('inbox.stat.partialFoot') : t('common.none')}
        />
      </div>

      {siteMonths === 0 && (
        <div style={{ marginBottom: 24 }}>
          <Callout tone="info">
{t('inbox.elapsedNote', { year })}
          </Callout>
        </div>
      )}

      {grouped.length === 0 ? (
        <Card>
          <Empty
            icon={X.Check}
            title={t('inbox.emptyTitle')}
            body={t('inbox.emptyBodyYear', { count: elapsed, year })}
            action={<Button variant="mint" icon={X.Plus} onClick={() => navigate('/regjistrimi')}>{t('overview.addData')}</Button>}
          />
        </Card>
      ) : (
        grouped.map(({ group, items }) => (
          <div key={group} style={{ marginBottom: 24 }}>
            <Card
              title={t(group)}
              sub={group === 'inbox.group.missing' ? t('inbox.group.missingSub')
                : group === 'inbox.group.partial' ? t('inbox.group.partialSub')
                  : undefined}
              right={<Badge tone={items[0].tone === 'e' ? 'err' : items[0].tone === 'o' ? 'ok' : 'warn'}>
                {items.length}
              </Badge>}
              bodyProps={{ style: { padding: '4px 0 0' } }}
            >
              {items.map((t) => <TaskRow key={t.id} task={t} />)}
            </Card>
          </div>
        ))
      )}
    </div>
  );
}

export default function ActionInboxPage() {
  return (
    <ErrorBoundary>
      <Shell>
        <ActionInbox />
      </Shell>
    </ErrorBoundary>
  );
}
